import { observer } from 'mobx-react';
import { FC, Fragment, useEffect } from 'react';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Card } from '@monorepo/base/src/components/card/card';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { IAdminxStore } from '../../../../stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from './basic-info.module.scss';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Spacer } from '@monorepo/base/src/components/spacer/spacer';
import { DropdownNew } from '@monorepo/base/src/components/dropdown-new/dropdown-new';
import { Flex } from '@monorepo/base/src/components/flex/flex';
import { GButton } from '@monorepo/base/src/components/buttons/group/button/g-button';
import { Distributions } from '../../../../enums/distribution';
import { ButtonGroup } from '@monorepo/base/src/components/buttons/group/button-group';

export const BasicInfo: FC = observer(() => {
	const { bidderStore } = useStores<IAdminxStore>();

	const campaignCrudStore = bidderStore.getCrud();
	const formStore = campaignCrudStore.getFormStore();
	const campaign = campaignCrudStore.getData().campaign;
	if (campaign === undefined) {
		return null;
	}

	useEffect(() => {
		if (campaign.campaign_advertiser_id) {
			bidderStore.campaignGroups.fetch(campaign.campaign_advertiser_id);
		}
	}, [campaign.campaign_advertiser_id]);

	return (
		<Card
			id={'basicInfo'}
			title={'Basic Info'}
			icon={'settings-01'}
			className={styles.wrapper}
			debugProps={{ dataAttrs: [new DataAttribute('id', 'basic_info_section')] }}>
			<Card>
				<div className={styles.inputsWrapper}>
					<Input
						className={styles.campaignName}
						value={campaign.campaign_name}
						placeholder={'Campaign Name'}
						onValue={value => {
							campaign.campaign_name = value;
							formStore.clearError('name');
						}}
						required
						error={formStore.getErrors().get('name')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'name')] }}
					/>
					<Input
						className={styles.campaignId}
						value={campaign.campaign_id?.toString()}
						placeholder={'Campaign ID'}
						disabled
						error={formStore.getErrors().get('name')?.getMsg()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'name')] }}
					/>
				</div>
				<Input
					value={campaign.campaign_daily_budget?.toString()}
					placeholder={'Daily Budget'}
					type={'number'}
					required
					onValue={value => {
						campaign.campaign_daily_budget = parseFloat(value ?? 0);
						formStore.clearError('dailyBudget');
					}}
				/>
				<Spacer />
				<ButtonGroup
					className={styles.buttonGroup}
					defaultValue={[Distributions.Eager, Distributions.Evenly][campaign.campaign_evenly_distribution || 0]}
					title={'Distribution'}
					onChange={(setValues: Set<string>) =>
						(campaign.campaign_evenly_distribution = setValues.values().next().value === Distributions.Eager ? 0 : 1)
					}>
					<GButton
						icon={'directions-run'}
						text={Distributions.Eager}
						value={Distributions.Eager}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'distribution_eager')] }}
					/>
					<GButton
						icon={'scales-02'}
						text={Distributions.Evenly}
						value={Distributions.Evenly}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'distribution_evenly')] }}
					/>
				</ButtonGroup>
				<Input
					value={campaign.campaign_max_loss_per_domain?.toString()}
					placeholder={'Max Loss per Domain'}
					type={'number'}
					required
					onValue={value => {
						campaign.campaign_max_loss_per_domain = parseFloat(value);
						formStore.clearError('maxLossPerDomain');
					}}
				/>
				<Input
					value={campaign.campaign_max_clicks_to_first_conv?.toString()}
					placeholder={'Max Clicks to First Conversion'}
					type={'number'}
					required
					onValue={value => {
						campaign.campaign_max_clicks_to_first_conv = parseInt(value);
						formStore.clearError('maxClicksToFirstConv');
					}}
				/>
				<Input
					value={campaign.campaign_q_max_loss_per_domain?.toString()}
					placeholder={'Q Max Loss per Domain'}
					type={'number'}
					required
					onValue={value => {
						campaign.campaign_q_max_loss_per_domain = parseInt(value);
						formStore.clearError('maxLossPerDomain');
					}}
				/>
				<Input
					value={campaign.campaign_q_max_clicks_to_first_conv?.toString()}
					placeholder={'Q Max Clicks to First Conversion'}
					type={'number'}
					required
					onValue={value => {
						campaign.campaign_q_max_clicks_to_first_conv = parseFloat(value);
						formStore.clearError('maxClicksToFirstConv');
					}}
				/>
				<Input
					value={campaign.campaign_weight?.toString()}
					placeholder={'Campaign Weight'}
					type={'number'}
					required
					min={0}
					max={100}
					error={formStore.getErrors().get('campaign_weight')?.getMsg()}
					onValue={value => {
						campaign.campaign_weight = parseInt(value);
						formStore.clearError('campaign_weight');
					}}
				/>
				<Spacer />
				<DropdownNew
					defaultValues={campaign.campaign_advertiser_id ? [campaign.campaign_advertiser_id] : []}
					label="Advertiser"
					required
					options={bidderStore.getActiveAdvertisers().map(i => ({ label: i.name, value: i.id }))}
					onSelect={v => {
						campaign.campaign_advertiser_id = v[0];
						campaign.campaign_group_id = undefined;
						formStore.clearError('campaign_advertiser_id');
					}}
					error={formStore.getErrors().get('campaign_advertiser_id')?.getMsg()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_advertiser_id')] }}
				/>
				<Spacer />
				{campaign.campaign_advertiser_id && (
					<Fragment>
						<DropdownNew
							defaultValues={campaign.campaign_group_id ? [campaign.campaign_group_id] : []}
							label="Campgin Group"
							required
							isLoading={bidderStore.campaignGroups.isLoading}
							disabled={bidderStore.campaignGroups.isLoading}
							options={bidderStore.getCampaignGroups().map(i => ({ label: i.name, value: i.id }))}
							onSelect={v => {
								campaign.campaign_group_id = v[0];
								formStore.clearError('campaign_group_id');
							}}
							error={formStore.getErrors().get('campaign_group_id')?.getMsg()}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_group_id')] }}
						/>
						<Spacer />
					</Fragment>
				)}
				<DropdownNew
					defaultValues={campaign.campaign_state_machine_id ? [campaign.campaign_state_machine_id] : []}
					label="State Machine ID"
					required
					isLoading={bidderStore.stateMachineIds?.isLoading ?? false}
					disabled={bidderStore.stateMachineIds?.isLoading ?? false}
					options={bidderStore.getStateMachineIds().map(stateMachineId => ({
						label: String(stateMachineId),
						value: stateMachineId,
					}))}
					onSelect={v => {
						campaign.campaign_state_machine_id = v[0];
						formStore.clearError('campaign_state_machine_id');
					}}
					error={formStore.getErrors().get('campaign_state_machine_id')?.getMsg()}
					debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_state_machine_id')] }}
				/>
				<Spacer />
				<Flex alignSelf={'start'}>
					<Toggle
						label={campaign.campaign_jm_exclusion === 1 ? 'Include' : 'Exclude'}
						title="Job Manager Exclusion"
						onChange={value => {
							campaign.campaign_jm_exclusion = value ? 1 : 0;
						}}
						disabled={campaign.campaign_add_stats_to_caches === 0}
						defaultValue={campaign.campaign_jm_exclusion === 1}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_status')] }}
					/>
				</Flex>
				<Spacer />

				<Flex alignSelf={'start'}>
					<Toggle
						label={campaign.campaign_parameters_update_suppressed ? 'Include' : 'Exclude'}
						title="Parameters Updater Exclusion"
						onChange={v => (campaign.campaign_parameters_update_suppressed = v ? 1 : 0)}
						defaultValue={campaign.campaign_parameters_update_suppressed === 1}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_parameters_update_suppressed')] }}
					/>
				</Flex>
				<Spacer />
				<Flex alignSelf={'start'}>
					<Toggle
						label={campaign.campaign_hide_referrer === 1 ? 'Include' : 'Exclude'}
						title="Hide Referrer"
						onChange={value => {
							campaign.campaign_hide_referrer = value ? 1 : 0;
						}}
						defaultValue={campaign.campaign_hide_referrer === 1}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_hide_referrer')] }}
					/>
				</Flex>
				<Spacer />
				<Flex alignSelf={'start'}>
					<Toggle
						label={campaign.campaign_add_stats_to_caches === 1 ? 'Include' : 'Exclude'}
						title="Add Stats to Cache"
						onChange={value => {
							campaign.campaign_add_stats_to_caches = value ? 1 : 0;
							campaign.campaign_jm_exclusion = value ? 0 : 1;
						}}
						defaultValue={campaign.campaign_add_stats_to_caches === 1}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_add_stats_to_cache')] }}
					/>
				</Flex>
			</Card>
		</Card>
	);
});
